/**
 * React Query hooks for Case Playbooks and Checklists
 *
 * Provides hooks for:
 * - Listing and fetching playbooks
 * - Getting checklist progress for a case
 * - Completing/uncompleting checklist items
 */

import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth';
import { ApiClient } from '../services/api';

// Query keys
export const playbookKeys = {
  all: ['playbooks'],
  lists: () => [...playbookKeys.all, 'list'],
  list: (filters) => [...playbookKeys.lists(), filters],
  details: () => [...playbookKeys.all, 'detail'],
  detail: (id) => [...playbookKeys.details(), id],
};

export const checklistKeys = {
  all: ['checklists'],
  forCase: (caseId) => [...checklistKeys.all, 'case', caseId],
};

// Service class
class PlaybooksService {
  constructor(getToken) {
    this.client = new ApiClient(getToken);
  }

  listPlaybooks(params = {}, options = {}) {
    const query = params.includeInactive ? '?include_inactive=true' : '';
    return this.client.get(`/case-management/playbooks${query}`, options);
  }

  getPlaybook(id, options = {}) {
    return this.client.get(`/case-management/playbooks/${id}`, options);
  }

  createPlaybook(data, options = {}) {
    return this.client.post('/case-management/playbooks', data, options);
  }

  updatePlaybook(id, data, options = {}) {
    return this.client.put(`/case-management/playbooks/${id}`, data, options);
  }

  deletePlaybook(id, options = {}) {
    return this.client.delete(`/case-management/playbooks/${id}`, options);
  }

  // Checklist endpoints
  getCaseChecklist(caseId, options = {}) {
    return this.client.get(`/case-management/cases/${caseId}/checklist`, options);
  }

  completeChecklistItem(caseId, itemIndex, options = {}) {
    return this.client.post(
      `/case-management/cases/${caseId}/checklist/items/${itemIndex}/complete`,
      {},
      options
    );
  }

  uncompleteChecklistItem(caseId, itemIndex, options = {}) {
    return this.client.post(
      `/case-management/cases/${caseId}/checklist/items/${itemIndex}/uncomplete`,
      {},
      options
    );
  }
}

function usePlaybooksService() {
  const { getToken } = useAuth();
  return useMemo(() => new PlaybooksService(getToken), [getToken]);
}

// ===========================================
// PLAYBOOK HOOKS
// ===========================================

/**
 * Fetch all playbooks for the tenant
 */
export function usePlaybooks(options = {}) {
  const service = usePlaybooksService();

  return useQuery({
    queryKey: playbookKeys.list(options),
    queryFn: ({ signal }) => service.listPlaybooks(options, { signal }),
    staleTime: 60000, // 1 minute
    select: (data) => data.playbooks || [],
  });
}

/**
 * Fetch a single playbook by ID
 */
export function usePlaybook(id, options = {}) {
  const service = usePlaybooksService();

  return useQuery({
    queryKey: playbookKeys.detail(id),
    queryFn: ({ signal }) => service.getPlaybook(id, { signal }),
    enabled: !!id,
    staleTime: 60000,
    ...options,
  });
}

/**
 * Create a new playbook
 */
export function useCreatePlaybook() {
  const service = usePlaybooksService();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => service.createPlaybook(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: playbookKeys.lists() });
    },
  });
}

/**
 * Update a playbook
 */
export function useUpdatePlaybook() {
  const service = usePlaybooksService();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }) => service.updatePlaybook(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: playbookKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: playbookKeys.lists() });
    },
  });
}

/**
 * Delete a playbook
 */
export function useDeletePlaybook() {
  const service = usePlaybooksService();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => service.deletePlaybook(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: playbookKeys.lists() });
    },
  });
}

// ===========================================
// CHECKLIST HOOKS
// ===========================================

/**
 * Fetch checklist progress for a case
 */
export function useCaseChecklist(caseId, options = {}) {
  const service = usePlaybooksService();

  return useQuery({
    queryKey: checklistKeys.forCase(caseId),
    queryFn: ({ signal }) => service.getCaseChecklist(caseId, { signal }),
    enabled: !!caseId,
    staleTime: 30000,
    ...options,
  });
}

/**
 * Mark a checklist item as complete
 */
export function useCompleteChecklistItem() {
  const service = usePlaybooksService();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ caseId, itemIndex }) => service.completeChecklistItem(caseId, itemIndex),

    // Optimistic update
    onMutate: async ({ caseId, itemIndex }) => {
      await queryClient.cancelQueries({ queryKey: checklistKeys.forCase(caseId) });

      const previousChecklist = queryClient.getQueryData(checklistKeys.forCase(caseId));

      if (previousChecklist) {
        const updatedItems = previousChecklist.items.map((item) =>
          item.index === itemIndex
            ? { ...item, completed: true, completed_at: new Date().toISOString() }
            : item
        );
        const completedCount = updatedItems.filter((i) => i.completed).length;

        queryClient.setQueryData(checklistKeys.forCase(caseId), {
          ...previousChecklist,
          items: updatedItems,
          completed_count: completedCount,
          all_completed: completedCount === previousChecklist.total_items,
        });
      }

      return { previousChecklist };
    },

    onError: (err, { caseId }, context) => {
      if (context?.previousChecklist) {
        queryClient.setQueryData(checklistKeys.forCase(caseId), context.previousChecklist);
      }
    },

    onSettled: (_, __, { caseId }) => {
      queryClient.invalidateQueries({ queryKey: checklistKeys.forCase(caseId) });
    },
  });
}

/**
 * Mark a checklist item as incomplete
 */
export function useUncompleteChecklistItem() {
  const service = usePlaybooksService();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ caseId, itemIndex }) => service.uncompleteChecklistItem(caseId, itemIndex),

    // Optimistic update
    onMutate: async ({ caseId, itemIndex }) => {
      await queryClient.cancelQueries({ queryKey: checklistKeys.forCase(caseId) });

      const previousChecklist = queryClient.getQueryData(checklistKeys.forCase(caseId));

      if (previousChecklist) {
        const updatedItems = previousChecklist.items.map((item) =>
          item.index === itemIndex
            ? { ...item, completed: false, completed_at: null, completed_by_name: null }
            : item
        );
        const completedCount = updatedItems.filter((i) => i.completed).length;

        queryClient.setQueryData(checklistKeys.forCase(caseId), {
          ...previousChecklist,
          items: updatedItems,
          completed_count: completedCount,
          all_completed: false,
        });
      }

      return { previousChecklist };
    },

    onError: (err, { caseId }, context) => {
      if (context?.previousChecklist) {
        queryClient.setQueryData(checklistKeys.forCase(caseId), context.previousChecklist);
      }
    },

    onSettled: (_, __, { caseId }) => {
      queryClient.invalidateQueries({ queryKey: checklistKeys.forCase(caseId) });
    },
  });
}
